import { Link } from "react-router";

import { ROUTES } from "@/app/routes";
import { StatusBadge, type StatusTone } from "@/components/common/StatusBadge";
import type { StockAlert } from "@/features/dashboard/hooks/useDashboard";
import { expiryLabel, type StockStatus } from "@/lib/expiry";
import { formatQuantity } from "@/lib/format";

const TONES: Record<Exclude<StockStatus, "ok">, StatusTone> = {
  expired: "danger",
  out: "danger",
  expiring: "warning",
};

const LABELS: Record<Exclude<StockStatus, "ok">, string> = {
  expired: "Périmé",
  out: "Épuisé",
  expiring: "Bientôt périmé",
};

export function StockAlerts({ alerts, limit = 6 }: { alerts: StockAlert[]; limit?: number }) {
  const visible = alerts.slice(0, limit);

  return (
    <section className="rounded-xl border border-border bg-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3">
        <h2 className="text-[15px] font-semibold">À surveiller</h2>
        {alerts.length > 0 ? (
          <p className="text-sm text-muted-foreground tabular-nums">
            {alerts.length} article{alerts.length > 1 ? "s" : ""}
          </p>
        ) : null}
      </div>

      {visible.length === 0 ? (
        <p className="px-4 pb-5 text-sm text-muted-foreground">Rien à signaler : aucun article périmé ni épuisé.</p>
      ) : (
        <>
          <ul className="border-t border-muted">
            {visible.map((alert) => {
              const status = alert.status as Exclude<StockStatus, "ok">;
              const base = alert.origin === "provision" ? ROUTES.provisionStocks : ROUTES.productStocks;

              return (
                <li key={alert.id} className="border-b border-muted last:border-b-0">
                  <Link
                    to={`${base}/${alert.stockId}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 transition hover:bg-muted/50"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px]">{alert.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {alert.origin === "provision" ? "Stock provisionnel" : "Stock marchand"}
                        {" · "}
                        reste {formatQuantity(alert.quantity, alert.measurementUnit)}
                        {alert.status !== "out" ? ` · ${expiryLabel(alert.expiryDate)}` : ""}
                      </span>
                    </span>
                    <StatusBadge tone={TONES[status]}>{LABELS[status]}</StatusBadge>
                  </Link>
                </li>
              );
            })}
          </ul>

          {alerts.length > visible.length ? (
            <p className="px-4 py-3 text-xs text-muted-foreground">
              et {alerts.length - visible.length} autre
              {alerts.length - visible.length > 1 ? "s" : ""}…
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
